import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import { toast } from 'sonner';
import { api, mensajeError } from '../lib/api';
import { useAuth } from '../lib/auth';
import { isAdmin } from '../lib/roles';
import { telefonoConPrefijo } from '../lib/utils';
import { PageHeader, EmptyState } from '../components/Common';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import {
  MessageCircle, Search, Send, Loader2, User, Check, CheckCheck, PlugZap, CornerUpLeft,
  AlertCircle, ArrowLeft, RefreshCw, Bot, Smartphone, FileText, Clock, Hand, UserRound,
} from 'lucide-react';
import ConexionWhatsApp from '../components/whatsapp/ConexionWhatsApp';
import PlantillasWhatsApp from '../components/whatsapp/PlantillasWhatsApp';
import BotWhatsApp from '../components/whatsapp/BotWhatsApp';
import {
  AVISO_SIN_ENLACE, BotPensando, BurbujaBorrador, FichaEstadoBot, LineaBorradorFallido, intercalarBorradores,
  quitarMarcadorEnlace, tieneMarcadorEnlace,
} from '../components/whatsapp/BorradoresBot';
import AvatarIniciales from '../components/AvatarIniciales';
// El aspecto de las burbujas es el del chat interno: los mismos colores,
// colita, color de nombre por persona, pastilla del día y hora.
import {
  COLOR_AJENA, COLOR_MIA, Colita, META, colorDeNombre, hora,
} from '../components/chat/BurbujaChat';
import { PASTILLA, etiquetaDia } from './ChatPage';

/**
 * Bandeja de WhatsApp (opción B): el número de la clínica vive en la API de
 * WhatsApp Business y el equipo lee y contesta desde aquí. Hasta que el número
 * esté conectado, la página funciona en modo "pendiente de conectar".
 *
 * Repasada el 30/9/2026, antes de conectar el número:
 *  - La tarjeta tiene alto fijo y cada columna se desplaza por dentro. Antes la
 *    página crecía con la conversación (5.000 px con 60 mensajes) y el
 *    `scrollIntoView` movía la ventana entera.
 *  - Solo se baja al final al abrir una conversación, al enviar o si ya
 *    estabas abajo: el refresco de cada 15 s ya no te arranca de lo que lees.
 *  - Cada mensaje de la clínica dice QUIÉN lo escribió (dirección: cada
 *    mensaje con el nombre de la persona del equipo) y por dónde salió (en
 *    bloque, vía Agente…). El paciente no ve nada de eso. Se ve como el chat
 *    interno (dirección: «parecido a lo del chat»): burbujas tipo WhatsApp,
 *    pastilla por día y el nombre en color, el mismo por persona que allí.
 *  - Un mensaje que WhatsApp no entregó sale en rojo con el porqué; antes
 *    tenía la misma rayita que uno enviado.
 *  - Fechas en hora local: hoy la hora, ayer «Ayer», esta semana el día.
 *  - En el móvil, lista o conversación (con «Volver»), no las dos apiladas.
 *  - Cargando, error con «Reintentar» y búsqueda sin resultados, cada uno
 *    con su texto.
 *
 * WhatsApp por API (1/11/2026, dirección: «prepara el menú WhatsApp para que
 * veamos qué escribe el bot y poder usarlo nosotros»):
 *  - «Conexión» (solo administradores): los datos de Meta, el aviso,
 *    «Probar» y los ajustes de dirección (components/whatsapp).
 *  - Cada mensaje dice quién lo escribió: el contacto, una persona del equipo
 *    con su nombre, el BOT (distintivo «Bot») o «Desde el móvil».
 *  - «Lo llevo yo»: cada conversación dice si la lleva el bot o el equipo, en
 *    la lista y en la cabecera, y se puede devolver al bot. Escribir la pasa
 *    al equipo (lo hace el servidor).
 *  - La ventana de 24 h: fuera de ella la caja se apaga y se ofrece mandar una
 *    plantilla aprobada.
 *  - De quién es el número: su ficha o su lead, con el enlace.
 *
 * El bot dentro de ClinicPilot (1/11/2026):
 *  - «Bot» (solo administradores): estado, clave, ajustes, instrucciones,
 *    simulador, plantillas, pruebas y métricas (components/whatsapp).
 *  - En la conversación, sus borradores (modo prueba) con borde discontinuo
 *    y «Borrador del bot · no enviado», que se pueden copiar a la caja; el
 *    intento fallido en una línea; «El bot está pensando…»; «¿Qué
 *    contestaría el bot?» si la lleva el bot, y la ficha con lo último que
 *    apuntó cuando ya envía.
 *  - En modo prueba, la cabecera de una conversación del bot dice «Modo
 *    prueba: el bot solo escribe borradores» (antes, «Sin bot: la lleva el
 *    equipo», mientras salían sus borradores debajo); y si el servidor dice
 *    que el bot no atiende esa conversación (es un paciente, otra marca…),
 *    esa línea, discreta. «¿Qué contestaría el bot?» solo si el bot no está
 *    apagado y atiende esa conversación, y «pensando» solo si la lleva el bot.
 *  - Copiar un borrador sin teléfono de cita no copia el marcador del enlace:
 *    avisa de que falta.
 */

const INTERVALO_MS = 15000;
// A menos de esto del final se considera que «estás abajo» y lo nuevo se ve solo.
const MARGEN_ABAJO_PX = 80;

const VIAS = {
  envio_en_bloque: 'envío en bloque',
  agente: 'vía Agente',
  conector: 'vía conector',
};
// Los que no son una persona con nombre: su distintivo.
const SIN_PERSONA = { automatico: 'Automático', bot: 'Bot', movil: 'Desde el móvil' };

function fecha(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : d;
}

function dosCifras(n) { return String(n).padStart(2, '0'); }

function hhmm(d) { return `${dosCifras(d.getHours())}:${dosCifras(d.getMinutes())}`; }

function diasAtras(d, ahora) {
  const hoy = new Date(ahora.getFullYear(), ahora.getMonth(), ahora.getDate());
  const ese = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  // Redondeado: los días del cambio de hora tienen 23 o 25 horas.
  return Math.round((hoy - ese) / 86400000);
}

/** La hora de la lista: hoy «10:32», ayer «Ayer», esta semana «lunes»,
 *  antes «21/9/26». Siempre en hora LOCAL. */
export function cuandoCorto(iso, ahora = new Date()) {
  const d = fecha(iso);
  if (!d) return '';
  const dias = diasAtras(d, ahora);
  if (dias <= 0) return hhmm(d);
  if (dias === 1) return 'Ayer';
  if (dias < 7) return d.toLocaleDateString('es-ES', { weekday: 'long' });
  return `${d.getDate()}/${d.getMonth() + 1}/${String(d.getFullYear()).slice(2)}`;
}

/** La hora junto a la burbuja: hoy solo la hora; si no, fecha y hora LOCALES
 *  (antes la fecha salía del texto UTC y la hora de la local: pasada la
 *  medianoche no cuadraban). */
export function fechaHora(iso, ahora = new Date()) {
  const d = fecha(iso);
  if (!d) return '';
  if (diasAtras(d, ahora) === 0) return hhmm(d);
  return `${d.getDate()}/${d.getMonth() + 1}/${d.getFullYear()} ${hhmm(d)}`;
}

/** Quién escribió un mensaje de la clínica, para el equipo: «Nombre»,
 *  «Nombre · envío en bloque», «Automático». Vacío en lo que entra. */
export function autorDelMensaje(m) {
  if (!m || m.dir !== 'out') return '';
  if (SIN_PERSONA[m.sent_via]) return SIN_PERSONA[m.sent_via];
  const nombre = m.sent_by_name || '';
  const via = VIAS[m.sent_via];
  return [nombre, via].filter(Boolean).join(' · ');
}

function TicksEstado({ m }) {
  if (m.dir !== 'out') return null;
  if (m.status === 'failed') {
    return <AlertCircle className="w-3.5 h-3.5 text-red-600" aria-label="No entregado" data-testid="wa-no-entregado" />;
  }
  // Los colores de WhatsApp: gris enviado/entregado, azul leído.
  if (m.status === 'read') return <CheckCheck className="w-4 h-4 text-[#53BDEB]" aria-label="Leído" />;
  if (m.status === 'delivered') return <CheckCheck className="w-4 h-4" aria-label="Entregado" />;
  return <Check className="w-4 h-4" aria-label="Enviado" />;
}

/** Quién firma la racha: la persona de la clínica (con su color, estable por
 *  persona, como en el chat interno) o el paciente. */
function claveDeAutor(m) {
  if (m.dir !== 'out') return 'paciente';
  return `${m.sent_by || m.sent_by_name || ''}|${m.sent_via || ''}`;
}

function claveDeDia(iso) {
  const d = fecha(iso);
  return d ? `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}` : '';
}

/** Cada mensaje con si abre día y si abre racha (mensajes seguidos del mismo
 *  autor, el mismo día). Solo el primero de la racha lleva nombre y colita. */
export function conRachas(mensajes) {
  let anterior = null;
  return (mensajes || []).map((m) => {
    const nuevoDia = !anterior || claveDeDia(anterior.created_at) !== claveDeDia(m.created_at);
    const primeroDeRacha = nuevoDia || claveDeAutor(anterior) !== claveDeAutor(m);
    anterior = m;
    return { m, nuevoDia, primeroDeRacha };
  });
}

/** Una burbuja de la conversación, con el aspecto de las del chat interno
 *  (components/chat/BurbujaChat.jsx): lo de la clínica a la derecha en verde
 *  claro, lo del paciente a la izquierda en blanco; nombre en color arriba y
 *  la hora abajo a la derecha, dentro. */
function BurbujaWhatsApp({ m, primeroDeRacha, paciente }) {
  const mia = m.dir === 'out';
  const fallida = mia && m.status === 'failed';
  const autor = mia ? autorDelMensaje(m) : paciente;
  // El paciente, en gris oscuro: los colores son para distinguir a las
  // personas de la clínica entre sí.
  const sinPersona = !!SIN_PERSONA[m.sent_via];
  const colorNombre = mia
    ? (sinPersona ? META : colorDeNombre(m.sent_by || m.sent_by_name))
    : 'text-[#54656F] dark:text-[#AEBAC1]';
  let color = mia ? COLOR_MIA : COLOR_AJENA;
  if (fallida) color = 'bg-red-50 text-[#111B21] ring-1 ring-red-300 dark:bg-red-950/40 dark:text-[#E9EDEF] dark:ring-red-800';
  return (
    <div className={`flex ${mia ? 'justify-end' : 'justify-start'} ${primeroDeRacha ? 'mt-2' : 'mt-0.5'}`}
      data-testid="wa-mensaje" data-racha={primeroDeRacha ? 'inicio' : 'sigue'}>
      <div className={`relative rounded-lg shadow-[0_1px_0.5px_rgba(11,20,26,0.13)] text-[14.5px] leading-[1.35]
        px-2.5 pt-1.5 pb-2 max-w-[85%] sm:max-w-[70%] ${color} ${
        primeroDeRacha ? (mia ? 'rounded-tr-none' : 'rounded-tl-none') : ''}`}
        title={`${autor ? `${autor} · ` : ''}${fechaHora(m.created_at)}`}>
        {primeroDeRacha && !fallida && <Colita mio={mia} />}
        {primeroDeRacha && autor && (
          <div className={`text-[13px] font-medium leading-5 flex items-center gap-1 ${colorNombre}`}
            data-testid={mia ? 'wa-autor' : 'wa-remitente'} data-via={mia ? (m.sent_via || '') : undefined}>
            {/* El bot y el móvil, con su distintivo: no son una persona. */}
            {m.sent_via === 'bot' && <Bot className="w-3.5 h-3.5" aria-hidden="true" />}
            {m.sent_via === 'movil' && <Smartphone className="w-3.5 h-3.5" aria-hidden="true" />}
            {autor}
          </div>
        )}
        {m.tipo === 'plantilla' && (
          <div className={`text-[11px] leading-4 mb-0.5 flex items-center gap-1 ${META}`} data-testid="wa-es-plantilla">
            <FileText className="w-3 h-3" aria-hidden="true" />
            Plantilla{m.plantilla?.nombre ? ` «${m.plantilla.nombre}»` : ''}
          </div>
        )}
        <div className="whitespace-pre-wrap break-words">
          {m.texto}
          {/* El sitio de la hora al final de la última línea, como en el chat. */}
          <span className="inline-block w-16 align-bottom" aria-hidden="true" />
        </div>
        {fallida && (
          <div className="mt-1 mb-2 text-[12.5px] leading-snug text-red-700 dark:text-red-300 font-medium" data-testid="wa-motivo-fallo">
            No entregado: {m.error?.texto || 'WhatsApp no lo ha podido entregar.'}
          </div>
        )}
        <span className={`absolute bottom-1 right-2 flex items-center gap-1 text-[11px] leading-none tabular-nums ${META}`}
          data-testid="wa-hora">
          {hora(m.created_at)}
          <TicksEstado m={m} />
        </span>
      </div>
    </div>
  );
}

function nombreDeConversacion(c, tel) {
  return c?.patient_name || c?.lead_nombre || telefonoConPrefijo(`+${tel || c?.tel || ''}`);
}

/** Lo que queda de la ventana de 24 h, para la cabecera: «quedan 21 h». */
export function quedaVentana(cierraIso, ahora = new Date()) {
  const d = fecha(cierraIso);
  if (!d) return '';
  const min = Math.floor((d - ahora) / 60000);
  if (min <= 0) return '';
  if (min < 60) return `quedan ${min} min`;
  return `quedan ${Math.floor(min / 60)} h`;
}

/** La cabecera solo sirve si es de verdad un objeto con la conversación. */
function comoDetalle(datos) {
  return datos && typeof datos === 'object' && !Array.isArray(datos) ? datos : null;
}

/** Quién lleva la conversación (1/11/2026): «Bot» o «Equipo». */
function ChipLleva({ lleva, testid }) {
  const bot = lleva !== 'equipo';
  return (
    <span data-testid={testid} data-lleva={bot ? 'bot' : 'equipo'}
      className={`inline-flex items-center gap-1 rounded-full px-1.5 py-px text-[10.5px] font-semibold ${
        bot ? 'bg-sky-100 text-sky-800 dark:bg-sky-950 dark:text-sky-200'
          : 'bg-amber-100 text-amber-900 dark:bg-amber-950 dark:text-amber-200'}`}>
      {bot ? <Bot className="w-3 h-3" aria-hidden="true" /> : <Hand className="w-3 h-3" aria-hidden="true" />}
      {bot ? 'Bot' : 'Equipo'}
    </span>
  );
}

export default function WhatsAppPage() {
  const { user } = useAuth();
  const admin = isAdmin(user);
  const [params, setParams] = useSearchParams();
  const [pestana, setPestana] = useState('conversaciones'); // conversaciones | bot | conexion
  const [conectado, setConectado] = useState(null); // null = comprobando
  // Lo demás de /estado: si el bot puede contestar (para los distintivos).
  const [botActivo, setBotActivo] = useState(false);
  // La cabecera de la conversación abierta: de quién es, quién la lleva y la
  // ventana de 24 h (GET /conversaciones/{tel}).
  const [detalle, setDetalle] = useState(null);
  const [plantillas, setPlantillas] = useState(false);
  const [cambiandoLleva, setCambiandoLleva] = useState(false);
  const [convs, setConvs] = useState([]);
  const [estadoLista, setEstadoLista] = useState('cargando'); // cargando | ok | error
  const [q, setQ] = useState('');
  const [telActivo, setTelActivo] = useState(null);
  const [mensajes, setMensajes] = useState([]);
  const [estadoHilo, setEstadoHilo] = useState('ok'); // cargando | ok | error
  const [texto, setTexto] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [pidiendoBorrador, setPidiendoBorrador] = useState(false);
  const hiloRef = useRef(null);
  const textoRef = useRef(null);
  const telActivoRef = useRef(null);
  const bajarRef = useRef(true);
  // El borrador es DE CADA CONVERSACIÓN (revisión del 30/9/2026): antes la
  // caja era una sola, y lo escrito para un paciente se quedaba puesto al
  // abrir a otro; un Enter y le llegaba a quien no era.
  const borradoresRef = useRef({});
  // Para el Enter: la ventana de la conversación abierta, al momento.
  const ventanaCerradaRef = useRef(false);

  const escribir = (valor) => {
    setTexto(valor);
    if (telActivoRef.current) borradoresRef.current[telActivoRef.current] = valor;
  };

  const cargarEstado = useCallback(() => {
    api.get('/api/whatsapp/estado')
      .then((r) => {
        setConectado(!!r.data?.conectado);
        setBotActivo(!!r.data?.bot_activo);
      })
      .catch(() => setConectado(false));
  }, []);

  useEffect(() => { cargarEstado(); }, [cargarEstado]);

  const cargarConvs = useCallback(() => {
    const params = q.trim() ? { q: q.trim() } : {};
    api.get('/api/whatsapp/conversaciones', { params })
      .then((r) => {
        setConvs(Array.isArray(r.data) ? r.data : []);
        setEstadoLista('ok');
      })
      // Lo que ya se veía se queda; solo cambia el aviso.
      .catch(() => setEstadoLista('error'));
  }, [q]);

  const cargarMensajes = useCallback((tel) => {
    if (!tel) return;
    api.get(`/api/whatsapp/conversaciones/${tel}/mensajes`)
      .then((r) => {
        // Si mientras tanto se ha abierto otra conversación, esta respuesta
        // ya no es de la que se ve.
        if (telActivoRef.current !== tel) return;
        setMensajes(Array.isArray(r.data) ? r.data : []);
        setEstadoHilo('ok');
      })
      .catch(() => {
        if (telActivoRef.current !== tel) return;
        setEstadoHilo('error');
      });
  }, []);

  const cargarDetalle = useCallback((tel) => {
    if (!tel) return;
    api.get(`/api/whatsapp/conversaciones/${tel}`)
      .then((r) => {
        if (telActivoRef.current !== tel) return;
        setDetalle(comoDetalle(r.data));
      })
      // Sin cabecera se puede seguir leyendo y escribiendo como siempre.
      .catch(() => {});
  }, []);

  useEffect(() => { const t = setTimeout(cargarConvs, q ? 300 : 0); return () => clearTimeout(t); }, [cargarConvs, q]);

  const abrir = useCallback((tel) => {
    telActivoRef.current = tel;
    bajarRef.current = true;
    setTelActivo(tel);
    setTexto(borradoresRef.current[tel] || '');
    setMensajes([]);
    setDetalle(null);
    setPlantillas(false);
    setEstadoHilo('cargando');
    // Abrirla la da por leída (el servidor pone unread=0): la lista lo dice ya.
    setConvs((cs) => cs.map((c) => (c.tel === tel ? { ...c, unread: 0 } : c)));
    cargarMensajes(tel);
    cargarDetalle(tel);
  }, [cargarMensajes, cargarDetalle]);

  // Desde la fila de Leads (?tel=34600…): se abre esa conversación.
  const telDeLaUrl = params.get('tel');
  useEffect(() => {
    if (telDeLaUrl && /^[0-9]{6,20}$/.test(telDeLaUrl) && telActivoRef.current !== telDeLaUrl) {
      setPestana('conversaciones');
      abrir(telDeLaUrl);
    }
  }, [telDeLaUrl, abrir]);

  const volver = () => {
    telActivoRef.current = null;
    setTelActivo(null);
    setTexto('');
    setMensajes([]);
    setDetalle(null);
    setPlantillas(false);
    if (params.get('tel')) {
      const p = new URLSearchParams(params);
      p.delete('tel');
      setParams(p);
    }
  };

  // Al final solo si toca (al abrir, al enviar o si ya estabas abajo), y
  // moviendo SOLO la columna de los mensajes, nunca la página.
  // Lo del bot (borradores, «pensando») también cuenta como algo nuevo.
  const nBorradores = detalle?.bot?.borradores?.length || 0;
  const botPensando = !!detalle?.bot?.pensando;
  useLayoutEffect(() => {
    const el = hiloRef.current;
    if (el && bajarRef.current) el.scrollTop = el.scrollHeight;
  }, [mensajes, nBorradores, botPensando]);

  const alDesplazar = () => {
    const el = hiloRef.current;
    if (!el) return;
    bajarRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < MARGEN_ABAJO_PX;
  };

  // La bandeja se refresca sola, como la agenda: los WhatsApps llegan cuando
  // llegan. Cada 15 s si la pestaña está a la vista.
  useEffect(() => {
    const tick = () => {
      if (document.hidden) return;
      cargarConvs();
      if (telActivo) {
        cargarMensajes(telActivo);
        cargarDetalle(telActivo);
      }
    };
    const id = setInterval(tick, INTERVALO_MS);
    window.addEventListener('focus', tick);
    return () => { clearInterval(id); window.removeEventListener('focus', tick); };
  }, [cargarConvs, cargarMensajes, cargarDetalle, telActivo]);

  const enviar = async () => {
    // A quién va se decide AL PULSAR: la conversación puede cambiar mientras
    // el envío tarda.
    const tel = telActivoRef.current;
    const cuerpo = (tel ? (borradoresRef.current[tel] ?? texto) : texto).trim();
    if (!cuerpo || !tel || enviando || ventanaCerradaRef.current) return;
    setEnviando(true);
    try {
      await api.post(`/api/whatsapp/conversaciones/${tel}/enviar`, { texto: cuerpo });
      // Se borra el borrador de ESA conversación, y solo si sigue siendo lo
      // que se ha enviado (si se ha seguido escribiendo, se queda).
      if ((borradoresRef.current[tel] || '').trim() === cuerpo) delete borradoresRef.current[tel];
      if (telActivoRef.current === tel) {
        setTexto(borradoresRef.current[tel] || '');
        bajarRef.current = true;
        cargarMensajes(tel);
        // Escribir la pasa al equipo: la cabecera lo dice ya.
        cargarDetalle(tel);
      }
      cargarConvs();
    } catch (e) {
      // El texto se queda en la caja: el motivo dice si se puede reintentar.
      toast.error(e?.response?.data?.detail || 'No se ha podido enviar el mensaje');
    } finally {
      setEnviando(false);
    }
  };

  // «Lo llevo yo» / «Devolver al bot» (queda en la conversación y en el rastro).
  const cambiarLleva = async (nuevo) => {
    const tel = telActivoRef.current;
    if (!tel || cambiandoLleva) return;
    setCambiandoLleva(true);
    try {
      const r = await api.put(`/api/whatsapp/conversaciones/${tel}/lleva`, { lleva: nuevo });
      if (telActivoRef.current === tel) setDetalle(comoDetalle(r.data));
      setConvs((cs) => cs.map((c) => (c.tel === tel ? { ...c, lleva: nuevo } : c)));
      toast.success(nuevo === 'bot' ? 'Conversación devuelta al bot' : 'La llevas tú: el bot no escribirá aquí');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se ha podido cambiar quién la lleva');
    } finally {
      setCambiandoLleva(false);
    }
  };

  // «¿Qué contestaría el bot?»: un borrador ya, sin enviar nada.
  const pedirBorrador = async () => {
    const tel = telActivoRef.current;
    if (!tel || pidiendoBorrador) return;
    setPidiendoBorrador(true);
    try {
      const r = await api.post(`/api/whatsapp/conversaciones/${tel}/bot/borrador`);
      const b = r.data?.borrador;
      if (b && telActivoRef.current === tel) {
        bajarRef.current = true;
        setDetalle((d) => (d ? {
          ...d,
          bot: { ...(d.bot || {}), borradores: [...(d.bot?.borradores || []).filter((x) => x.id !== b.id), b] },
        } : d));
      }
      if (b?.error) toast.error(`El bot no ha podido preparar el borrador: ${typeof b.error === 'string' ? b.error : (b.error.motivo || '')}`);
      else toast.success('Borrador listo al final de la conversación: no se ha enviado nada');
    } catch (e) {
      toast.error(mensajeError(e, 'El bot no ha podido preparar un borrador'));
    } finally {
      setPidiendoBorrador(false);
    }
  };

  const copiarBorrador = (b) => {
    const unido = (b.mensajes || []).join('\n\n');
    // Sin teléfono de cita, el servidor deja el marcador del enlace: no se
    // copia (le llegaría tal cual al paciente) y se dice qué falta.
    const sinEnlace = tieneMarcadorEnlace(unido);
    escribir(sinEnlace ? quitarMarcadorEnlace(unido) : unido);
    if (textoRef.current && !textoRef.current.disabled) textoRef.current.focus();
    if (sinEnlace) toast.warning(AVISO_SIN_ENLACE);
    else toast.success('Copiado al cuadro de texto: revísalo antes de enviar');
  };

  const plantillaEnviada = () => {
    const tel = telActivoRef.current;
    setPlantillas(false);
    if (!tel) return;
    bajarRef.current = true;
    cargarMensajes(tel);
    cargarDetalle(tel);
    cargarConvs();
  };

  const convActiva = convs.find((c) => c.tel === telActivo);
  const buscando = q.trim().length > 0;
  // De quién es el número: la cabecera (al momento) o lo guardado en la lista.
  const fichaId = detalle ? detalle.patient_id : convActiva?.patient_id;
  const fichaNombre = detalle ? detalle.patient_name : convActiva?.patient_name;
  const lead = detalle?.lead || null;
  const nombreActivo = fichaNombre || lead?.nombre || convActiva?.lead_nombre
    || telefonoConPrefijo(`+${telActivo || ''}`);
  const ventana = detalle?.ventana || null;
  // Solo se apaga la caja si se SABE que la ventana está cerrada.
  const ventanaCerrada = !!ventana && ventana.abierta === false;
  ventanaCerradaRef.current = ventanaCerrada;
  const lleva = detalle?.lleva || convActiva?.lleva || 'bot';
  // Lo del bot en esta conversación (borradores, si piensa, su ficha).
  const botConv = detalle?.bot && typeof detalle.bot === 'object' ? detalle.bot : null;
  // El modo del bot lo manda el servidor en `detalle.bot.modo`. Solo si no
  // llega (un servidor anterior) se saca de si el bot puede contestar (el
  // `bot_activo` del detalle, o el de /estado).
  const puedeContestar = typeof detalle?.bot_activo === 'boolean' ? detalle.bot_activo : botActivo;
  const modoBot = botConv ? (botConv.modo || (puedeContestar ? 'envia' : 'prueba')) : null;
  // Por qué el bot no atiende esta conversación (es un paciente, otra
  // marca…), si lo dice el servidor.
  const noAtiende = typeof botConv?.no_atiende === 'string' && botConv.no_atiende.trim() ? botConv.no_atiende.trim() : '';
  // Modo prueba en una conversación del bot: solo deja borradores.
  const enPrueba = modoBot === 'prueba' && lleva !== 'equipo' && !noAtiende;
  const enlaceLead = lead
    ? `/leads?${lead.vista && lead.vista !== 'pendientes' ? `vista=${lead.vista}&` : ''}lead=${lead.id}`
    : null;

  let lista;
  if (convs.length === 0 && estadoLista === 'cargando') {
    lista = (
      <div className="p-6 flex items-center justify-center gap-2 text-sm text-muted-foreground" data-testid="wa-cargando">
        <Loader2 className="w-4 h-4 animate-spin" /> Cargando conversaciones…
      </div>
    );
  } else if (convs.length === 0 && estadoLista === 'error') {
    lista = (
      <div className="p-6 text-center text-sm" data-testid="wa-error-lista">
        <div className="text-[#13231E] font-medium">No se han podido cargar las conversaciones.</div>
        <Button size="sm" variant="outline" className="mt-3" onClick={cargarConvs}>
          <RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Reintentar
        </Button>
      </div>
    );
  } else if (convs.length === 0) {
    lista = (
      <div className="p-6 text-center text-sm text-muted-foreground" data-testid="wa-lista-vacia">
        {buscando
          ? `Ninguna conversación coincide con «${q.trim()}».`
          : conectado === false
            ? 'Todavía no hay conversaciones: llegarán aquí cuando el número esté conectado.'
            : 'Todavía no hay conversaciones.'}
      </div>
    );
  } else {
    lista = convs.map((c) => {
      // Sin ficha, el número ES el nombre de la fila: con más razón se lee
      // con sus huecos.
      const quien = nombreDeConversacion(c);
      const activa = telActivo === c.tel;
      return (
        <button
          key={c.tel}
          type="button"
          onClick={() => abrir(c.tel)}
          aria-current={activa ? 'true' : undefined}
          className={`w-full text-left px-2 py-2 rounded-xl flex items-start gap-2.5
            transition-colors duration-150
            focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#2E6E5E]
            ${activa ? 'bg-[rgba(46,110,94,0.10)]' : 'hover:bg-muted/50'}`}
          data-testid="wa-conversacion"
        >
          {/* Avatar de color estable: antes eran todas las filas iguales
              y había que leer para encontrar a alguien. */}
          <AvatarIniciales nombre={quien} size={38} />
          <span className="flex-1 min-w-0">
            <span className="flex items-baseline justify-between gap-2">
              <span className="flex items-center gap-1.5 min-w-0">
                <span className={`truncate ${c.unread > 0 ? 'font-semibold text-[#0B1220]' : 'font-medium text-[#13231E]'}`}>
                  {quien}
                </span>
                {!c.patient_name && c.lead_nombre && (
                  <span className="shrink-0 rounded px-1 text-[10px] font-semibold bg-violet-100 text-violet-800"
                    data-testid="wa-es-lead">Lead</span>
                )}
                {/* Quién la lleva, solo si el bot puede contestar: sin bot,
                    todas las lleva el equipo. */}
                {botActivo && <ChipLleva lleva={c.lleva} testid="wa-fila-lleva" />}
              </span>
              <span className="text-[11px] text-muted-foreground shrink-0" data-testid="wa-cuando">{cuandoCorto(c.last_at)}</span>
            </span>
            <span className="flex items-center justify-between gap-2 mt-0.5">
              <span className={`text-xs truncate ${c.unread > 0 ? 'text-[#13231E]' : 'text-muted-foreground'}`}>
                {c.last_dir === 'out' && <CornerUpLeft className="w-3 h-3 inline mr-1 -mt-px" />}
                {c.last_dir === 'out' && c.last_by_name ? `${c.last_by_name}: ` : ''}
                {c.last_text}
              </span>
              {c.unread > 0 && (
                <span className="shrink-0 min-w-[20px] h-5 px-1.5 rounded-full bg-[#2E6E5E] text-white text-[11px] font-bold flex items-center justify-center"
                  aria-label={`${c.unread} sin leer`}>
                  {c.unread}
                </span>
              )}
            </span>
          </span>
        </button>
      );
    });
  }

  return (
    <div>
      <PageHeader
        title="WhatsApp"
        subtitle="Conversaciones con pacientes y contactos por el número de la clínica."
        actions={admin ? (
          <div className="inline-flex rounded-full border p-0.5" role="tablist" aria-label="WhatsApp">
            {[['conversaciones', 'Conversaciones'], ['bot', 'Bot'], ['conexion', 'Conexión']].map(([clave, nombre]) => (
              <button key={clave} type="button" role="tab" aria-selected={pestana === clave}
                onClick={() => { setPestana(clave); if (clave === 'conversaciones') cargarEstado(); }}
                data-testid={`wa-pestana-${clave}`}
                className={`rounded-full px-3 py-1 text-sm ${pestana === clave
                  ? 'bg-[#2E6E5E] text-white' : 'text-muted-foreground hover:text-foreground'}`}>
                {nombre}
              </button>
            ))}
          </div>
        ) : null}
      />

      {admin && pestana === 'conexion' ? (
        <ConexionWhatsApp />
      ) : admin && pestana === 'bot' ? (
        <BotWhatsApp onIrAConexion={() => setPestana('conexion')} />
      ) : (
      <>

      {conectado === false && (
        <div className="cjo-card p-4 mb-4 border-l-4 border-l-amber-500 bg-amber-50/50 dark:bg-amber-950/20 flex items-start gap-3" data-testid="wa-sin-conectar">
          <PlugZap className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
          <div className="text-sm">
            <div className="font-semibold text-[#13231E]">Pendiente de conectar</div>
            <div className="text-muted-foreground">
              La bandeja está lista, pero el número de la clínica todavía no está conectado a la
              API de WhatsApp Business. Mientras tanto no se puede enviar desde aquí ni entra nada.
              {admin ? ' Se conecta en la pestaña «Conexión».' : ''}
            </div>
          </div>
        </div>
      )}

      {/* Alto fijo: cada columna se desplaza por dentro (con el aviso de
          «pendiente de conectar» encima, un poco menos). */}
      <div className={`cjo-card overflow-hidden flex min-h-[26rem] ${conectado === false ? 'h-[calc(100dvh-19rem)]' : 'h-[calc(100dvh-12rem)]'}`}
        data-testid="wa-bandeja">
        {/* Lista de conversaciones (en el móvil, solo si no hay una abierta) */}
        <div className={`${telActivo ? 'hidden md:flex' : 'flex'} w-full md:w-[320px] md:shrink-0 border-r border-border flex-col min-h-0`}
          data-testid="wa-columna-lista">
          <div className="p-2.5 border-b border-border shrink-0">
            <div className="relative">
              <Search className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar por nombre, teléfono o texto…"
                aria-label="Buscar conversaciones" className="pl-8 h-9" data-testid="wa-buscar" />
            </div>
          </div>
          {estadoLista === 'error' && convs.length > 0 && (
            <div className="px-3 py-1.5 text-[11px] text-amber-700 bg-amber-50 border-b border-border shrink-0" data-testid="wa-aviso-refresco">
              Sin conexión con el servidor: lo que ves puede no estar al día. Se reintenta solo.
            </div>
          )}
          <div className="flex-1 min-h-0 overflow-y-auto p-1.5 space-y-0.5">
            {lista}
          </div>
        </div>

        {/* Hilo (en el móvil, solo si hay una abierta) */}
        <div className={`${telActivo ? 'flex' : 'hidden md:flex'} flex-1 min-w-0 flex-col min-h-0`} data-testid="wa-columna-hilo">
          {!telActivo ? (
            <div className="flex-1 flex items-center justify-center p-4">
              <EmptyState icon={MessageCircle} title="Elige una conversación" description="Las conversaciones están a la izquierda." />
            </div>
          ) : (
            <>
              <div className="px-2 md:px-4 py-2.5 border-b border-border flex items-center justify-between gap-2 shrink-0">
                <div className="min-w-0 flex items-center gap-2 md:gap-3">
                  <Button size="icon" variant="ghost" className="md:hidden shrink-0" onClick={volver}
                    aria-label="Volver a las conversaciones" data-testid="wa-volver">
                    <ArrowLeft className="w-4 h-4" />
                  </Button>
                  <AvatarIniciales nombre={nombreActivo} size={36} />
                  <div className="min-w-0">
                    <div className="font-semibold text-[#13231E] truncate" data-testid="wa-cabecera-nombre">{nombreActivo}</div>
                    <div className="text-xs text-muted-foreground" data-testid="wa-subtitulo">
                      {fichaNombre || lead
                        ? `${telefonoConPrefijo(`+${telActivo}`)}${!fichaNombre && lead ? ' · lead, sin ficha de paciente' : ''}`
                        : 'Sin ficha de paciente ni lead con este número'}
                    </div>
                    {/* La ventana de 24 h y quién la lleva. */}
                    <div className="flex flex-wrap items-center gap-1.5 mt-0.5">
                      {ventana && (
                        <span data-testid="wa-ventana" data-abierta={ventana.abierta ? 'si' : 'no'}
                          className={`inline-flex items-center gap-1 rounded-full px-1.5 py-px text-[10.5px] font-semibold ${
                            ventana.abierta ? 'bg-emerald-100 text-emerald-800' : 'bg-amber-100 text-amber-900'}`}>
                          <Clock className="w-3 h-3" aria-hidden="true" />
                          {ventana.abierta
                            ? `Ventana abierta${quedaVentana(ventana.cierra_at) ? ` · ${quedaVentana(ventana.cierra_at)}` : ''}`
                            : 'Ventana cerrada · solo plantillas'}
                        </span>
                      )}
                      {noAtiende ? (
                        <span className="inline-flex items-center gap-1 text-[11px] text-muted-foreground"
                          data-testid="wa-bot-no-atiende">
                          <Bot className="w-3 h-3" aria-hidden="true" />
                          {noAtiende}
                        </span>
                      ) : null}
                      {detalle && (enPrueba ? (
                        <span className="inline-flex items-center gap-1 text-[11px] font-medium text-sky-800 dark:text-sky-300"
                          data-testid="wa-lleva-prueba" title="El bot no envía nada: lo enciende dirección en WhatsApp → Conexión">
                          <Bot className="w-3 h-3" aria-hidden="true" />
                          Modo prueba: el bot solo escribe borradores
                        </span>
                      ) : botActivo ? (
                        <span className="inline-flex items-center gap-1.5" data-testid="wa-lleva">
                          <ChipLleva lleva={lleva} testid="wa-cabecera-lleva" />
                          <span className="text-[11px] text-muted-foreground">
                            {lleva === 'equipo'
                              ? `Lo lleva el equipo${detalle.lleva_por ? ` (${detalle.lleva_por})` : ''}: el bot no escribe aquí`
                              : 'Bot activo en esta conversación'}
                          </span>
                        </span>
                      ) : (
                        <span className="text-[11px] text-muted-foreground" data-testid="wa-lleva-sin-bot"
                          title="El bot está apagado en WhatsApp → Conexión">
                          Sin bot: la lleva el equipo
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
                <div className="flex flex-wrap items-center justify-end gap-1.5 shrink-0">
                  {detalle && botActivo && (lleva === 'equipo' ? (
                    <Button size="sm" variant="outline" onClick={() => cambiarLleva('bot')} disabled={cambiandoLleva}
                      data-testid="wa-devolver-bot">
                      <Bot className="w-3.5 h-3.5 mr-1.5" /> Devolver al bot
                    </Button>
                  ) : (
                    <Button size="sm" variant="outline" onClick={() => cambiarLleva('equipo')} disabled={cambiandoLleva}
                      data-testid="wa-lo-llevo-yo">
                      <Hand className="w-3.5 h-3.5 mr-1.5" /> Lo llevo yo
                    </Button>
                  ))}
                  {fichaId && (
                    <Button size="sm" variant="outline" asChild data-testid="wa-ver-ficha">
                      <Link to={`/pacientes/${fichaId}`} target="_blank" rel="noopener">
                        <User className="w-3.5 h-3.5 mr-1.5" /> Ver ficha
                      </Link>
                    </Button>
                  )}
                  {enlaceLead && (
                    <Button size="sm" variant="outline" asChild data-testid="wa-ver-lead">
                      <Link to={enlaceLead} target="_blank" rel="noopener">
                        <UserRound className="w-3.5 h-3.5 mr-1.5" /> Ver lead
                      </Link>
                    </Button>
                  )}
                </div>
              </div>
              {botConv?.estado ? <FichaEstadoBot estado={botConv.estado} at={botConv.estado_at} /> : null}
              <div ref={hiloRef} onScroll={alDesplazar}
                className="flex-1 min-h-0 overflow-y-auto px-3 sm:px-[4%] py-3 bg-[#EFEAE2] dark:bg-[#0B141A]"
                data-testid="wa-hilo">
                {estadoHilo === 'cargando' && mensajes.length === 0 && (
                  <div className="h-full flex items-center justify-center gap-2 text-sm text-muted-foreground">
                    <Loader2 className="w-4 h-4 animate-spin" /> Cargando mensajes…
                  </div>
                )}
                {estadoHilo === 'error' && mensajes.length === 0 && (
                  <div className="h-full flex flex-col items-center justify-center text-sm" data-testid="wa-error-hilo">
                    <div className="text-[#13231E] font-medium">No se han podido cargar los mensajes.</div>
                    <Button size="sm" variant="outline" className="mt-3" onClick={() => cargarMensajes(telActivo)}>
                      <RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Reintentar
                    </Button>
                  </div>
                )}
                {/* Como el chat interno (dirección, 30/9/2026: «parecido a lo
                    del chat, que aparece el nombre de cada uno»): separador por
                    día, rachas, la colita y el NOMBRE EN COLOR arriba del primer
                    mensaje de cada racha. Aquí lleva nombre también lo de la
                    clínica: todas escriben desde el mismo número. */}
                {/* Los borradores del bot van entre los mensajes, por su hora. */}
                {intercalarBorradores(conRachas(mensajes), botConv?.borradores).map((fila) => {
                  if (fila.tipo === 'borrador') {
                    const { b } = fila;
                    return b.error
                      ? <LineaBorradorFallido key={`b-${b.id}`} b={b} />
                      : <BurbujaBorrador key={`b-${b.id}`} b={b} onCopiar={copiarBorrador} />;
                  }
                  const { m, nuevoDia, primeroDeRacha } = fila;
                  return (
                    <React.Fragment key={m.id}>
                      {nuevoDia && (
                        <div className="flex justify-center pt-3 pb-1 first:pt-0" data-testid="wa-dia">
                          <span className={PASTILLA}>{etiquetaDia(m.created_at)}</span>
                        </div>
                      )}
                      <BurbujaWhatsApp m={m} primeroDeRacha={primeroDeRacha}
                        paciente={nombreActivo} />
                    </React.Fragment>
                  );
                })}
                {botConv?.pensando && lleva !== 'equipo' ? <BotPensando /> : null}
              </div>
              <div className="p-3 border-t border-border shrink-0 space-y-2">
                {plantillas && conectado && (
                  <PlantillasWhatsApp tel={telActivo} onEnviada={plantillaEnviada}
                    onCerrar={() => setPlantillas(false)} />
                )}
                {/* Si el bot no atiende esta conversación (paciente, otra marca…)
                    el servidor contesta 409: no se ofrece. */}
                {botConv && lleva !== 'equipo' && modoBot !== 'apagado' && !noAtiende && (
                  <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
                    <Button type="button" size="sm" variant="outline" onClick={pedirBorrador}
                      disabled={pidiendoBorrador || !!botConv.pensando} data-testid="wa-que-contestaria"
                      className="h-7" aria-describedby="wa-que-contestaria-nota">
                      {pidiendoBorrador
                        ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                        : <Bot className="w-3.5 h-3.5 mr-1.5" />}
                      {pidiendoBorrador ? 'Preguntando al bot…' : '¿Qué contestaría el bot?'}
                    </Button>
                    <span id="wa-que-contestaria-nota" className="hidden sm:inline text-[11px] text-muted-foreground">
                      Sale como borrador; no se envía nada.
                    </span>
                  </div>
                )}
                {conectado && ventanaCerrada && (
                  <div className="text-[12px] text-amber-900 bg-amber-50 border border-amber-200 rounded-md px-2.5 py-1.5"
                    data-testid="wa-aviso-ventana">
                    Han pasado más de 24 horas desde el último mensaje de este contacto (o no ha escrito nunca):
                    WhatsApp solo deja escribirle con una plantilla aprobada.
                  </div>
                )}
                <div className="flex items-end gap-2">
                  <Textarea
                    ref={textoRef}
                    value={texto}
                    onChange={(e) => escribir(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); enviar(); } }}
                    placeholder={!conectado ? 'Pendiente de conectar el número'
                      : ventanaCerrada ? 'Fuera de las 24 h: solo una plantilla' : 'Escribe un mensaje…'}
                    title="Enter envía; Mayúsculas+Enter salta de línea"
                    disabled={!conectado || ventanaCerrada}
                    aria-label="Mensaje para el paciente"
                    className="min-h-[42px] max-h-32 resize-none"
                    data-testid="wa-texto"
                  />
                  {conectado && (
                    <Button type="button" variant="outline" onClick={() => setPlantillas((v) => !v)}
                      aria-label="Mandar una plantilla" title="Mandar una plantilla aprobada"
                      className={`h-[42px] ${ventanaCerrada ? 'border-[#2E6E5E] text-[#2E6E5E]' : ''}`}
                      data-testid="wa-abrir-plantillas">
                      <FileText className="w-4 h-4" />
                      {ventanaCerrada ? <span className="ml-1.5">Mandar plantilla</span> : null}
                    </Button>
                  )}
                  <Button onClick={enviar} disabled={!conectado || ventanaCerrada || enviando || !texto.trim()}
                    aria-label="Enviar" className="bg-[#2E6E5E] hover:bg-[#1E4A3F] h-[42px]" data-testid="wa-enviar">
                    {enviando ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                  </Button>
                </div>
                {conectado && user?.name && (
                  <div className="text-[11px] text-muted-foreground" data-testid="wa-firma">
                    El paciente lo recibe de la clínica; aquí queda firmado como <span className="font-semibold">{user.name}</span>.
                    {botActivo && lleva !== 'equipo' ? ' Al escribir, el bot se para en esta conversación.' : ''}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
      </>
      )}
    </div>
  );
}
